import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import Button from "./Button";
import { preventOrphan, ensureTwoLines, formatProductTitle } from '../utils/text/textFormatting';
import { cn } from '../utils/cn';
import { toUrlFriendly } from "../utils/urlHelpers";

const shimmerStyle = {
  animation: "shimmer 2s infinite linear",
  background: "linear-gradient(to right, #e0e0e0 8%, #f0f0f0 18%, #e0e0e0 33%)",
  backgroundSize: "1000px 100%",
};

function SkeletonBlock({ className, style }) {
  return (
    <div
      className={`${className} relative overflow-hidden bg-[#A9AAAC] rounded-md`}
      style={{ ...shimmerStyle, ...style }}
    />
  );
}

const ProductCard = ({ product, sequence = 0, isProfessional = false, onRequestQuote }) => {
  const navigate = useNavigate();
  const [isFlipped, setIsFlipped] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isTextLoaded, setIsTextLoaded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const hasProduct = product && product.fields;
  const [localProduct, setLocalProduct] = useState(null);

  const cardRef = React.useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-100px" });

  // Disable flip for industrial pages
  const isIndustrial = isProfessional || window.location.pathname.includes('/industrial');
  const shouldUseFlipCard = isMobile || !isIndustrial;

  // Animation variants for sequential animations
  const imageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 + (sequence * 0.1),
        duration: 0.2,
        ease: "easeOut"
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 + (sequence * 0.1) + (i * 0.03),
        duration: 0.2,
        ease: "easeOut"
      }
    })
  };

  useEffect(() => {
    if (hasProduct) {
      setLocalProduct(product);
    }
  }, [hasProduct, product]);

  useEffect(() => {
    if (localProduct && localProduct.fields) {
      setIsTextLoaded(true);
    }
  }, [localProduct]);

  // When the card is flipped and the user leaves the card, flip it back after a short delay.
  useEffect(() => {
    if (isFlipped && !isHovering) {
      const timer = setTimeout(() => {
        setIsFlipped(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isFlipped, isHovering]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { sys } = localProduct || {};
  const {
    productTitle,
    slogan,
    productHeroImage,
    price,
    buyNowButtonUrl,
    shortProductDescription,
    detailedProductDescription,
  } = (localProduct && localProduct.fields) || {};

  const imageUrl = productHeroImage?.fields?.file?.url || null;
  
  const handleCardClick = (e) => {
    // Only flip if clicking on non-button elements and not in industrial context
    if (
      e.target.tagName === "BUTTON" || 
      e.target.closest("button") ||
      e.target.tagName === "A" || 
      e.target.closest("a") ||
      isIndustrial
    ) {
      return;
    }
    setIsFlipped((prev) => !prev);
  };

  const handleMoreDetails = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const urlFriendlyTitle = toUrlFriendly(productTitle);
    navigate(`/${isProfessional ? 'industrial' : 'household'}/product/${urlFriendlyTitle}`);
  };

  if (!hasProduct) {
    return (
      <div className="relative w-full" style={{ height: "500px" }}>
        <div className="w-full h-full bg-white rounded-3xl shadow-md p-3">
          <SkeletonBlock className="w-full h-72 mb-2" />
          <SkeletonBlock className="w-48 h-6 mb-2" />
          <SkeletonBlock className="w-56 h-4 mb-2" />
          <SkeletonBlock className="w-24 h-5 mb-2" />
          <SkeletonBlock className="w-32 h-8 mb-1" />
          <SkeletonBlock className="w-32 h-8" />
        </div>
      </div>
    );
  }

  // Desktop version
  if (!isMobile) {
    return (
      <div
        className="relative w-full"
        style={{
          perspective: "1000px",
          height: "500px",
        }}
        onClick={handleCardClick}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <motion.div
          className="w-full h-full relative"
          style={{ transformStyle: "preserve-3d" }}
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          initial={{ rotateY: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut", delay: sequence * 0.1 }}
        >
          {/* FRONT SIDE */}
          <div
            className="absolute w-full h-full bg-white rounded-3xl shadow-md p-3"
            style={{ backfaceVisibility: "hidden" }}
          >
            <AnimatePresence>
              {(!isImageLoaded || !isTextLoaded) && (
                <motion.div
                  key="skeleton"
                  className="absolute inset-0 flex flex-col items-center justify-center bg-white rounded-3xl"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, transition: { duration: 0.5 } }}
                >
                  {!isImageLoaded && (
                    <SkeletonBlock className="w-full h-72 mb-2" />
                  )}
                  {!isTextLoaded && (
                    <>
                      <SkeletonBlock className="w-48 h-6 mb-2" />
                      <SkeletonBlock className="w-56 h-4 mb-2" />
                      <SkeletonBlock className="w-24 h-5 mb-2" />
                      <SkeletonBlock className="w-32 h-8 mb-1" />
                      <SkeletonBlock className="w-32 h-8" />
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isImageLoaded && isTextLoaded ? 1 : 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center justify-center gap-2 w-full h-full"
            >
              {/* IMAGE + TITLE + SLOGAN */}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={productTitle || "Product Image"}
                  className="w-48 h-48 object-cover rounded-lg"
                  onLoad={() => setIsImageLoaded(true)}
                />
              )}

              {/* Product Title */}
              {isTextLoaded && productTitle && (
                <h3
                  className="text-[1.4rem] font-bold text-gray-900 tracking-wide leading-tight text-center"
                  style={{
                    fontFamily: "Geogrotesque, sans-serif",
                    textTransform: "uppercase",
                  }}
                >
                  {productTitle}
                </h3>
              )}

              {/* Slogan */}
              {isTextLoaded && slogan && (
                <p
                  className="text-base italic text-gray-700"
                  style={{ fontFamily: "Open Sans, sans-serif" }}
                >
                  {slogan}
                </p>
              )}

              {/* Price */}
              {isTextLoaded && typeof price === "number" && !isProfessional && (
                <p
                  className="text-lg font-bold text-gray-900"
                  style={{ fontFamily: "Geogrotesque, sans-serif" }}
                >
                  ${price.toFixed(2)}
                </p>
              )}

              {/* Buttons */}
              <div className="flex flex-col gap-4 mx-auto mt-4">
                {!isProfessional && isTextLoaded && buyNowButtonUrl && (
                  <Button
                    to={buyNowButtonUrl === "Coming Soon!" ? "Coming Soon!" : undefined}
                    href={buyNowButtonUrl !== "Coming Soon!" ? buyNowButtonUrl : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary"
                    size="large"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      if (buyNowButtonUrl !== "Coming Soon!") {
                        window.open(buyNowButtonUrl, '_blank');
                      }
                    }}
                  >
                    {buyNowButtonUrl === "Coming Soon!" ? "Coming Soon!" : "Buy Now"}
                  </Button>
                )}
                {isTextLoaded && sys?.id && (
                  <Link
                    to={`/${isProfessional ? 'industrial' : 'household'}/product/${toUrlFriendly(productTitle)}`}
                    className="w-full"
                  >
                    <Button
                      variant={isProfessional ? "primary" : "secondary"}
                      size="large"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      More Details
                    </Button>
                  </Link>
                )}
              </div>
            </motion.div>
          </div>

          {/* BACK SIDE */}
          <div
            className="absolute w-full h-full bg-white rounded-3xl shadow-md p-3 flex flex-col justify-center items-center text-center"
            style={{
              transform: "rotateY(180deg)",
              backfaceVisibility: "hidden",
            }}
          >
            {detailedProductDescription ? (
              <>
                <h3
                  className="text-lg font-semibold text-gray-900 mb-2"
                  style={{ fontFamily: "Geogrotesque, sans-serif" }}
                >
                  Product Details
                </h3>
                <p
                  className="text-gray-700 leading-relaxed text-sm overflow-auto px-2"
                  style={{ fontFamily: "Geogrotesque, sans-serif" }}
                >
                  {detailedProductDescription}
                </p>
              </>
            ) : (
              <div className="flex-grow flex justify-center items-center text-gray-500 text-sm">
                No detailed description available.
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // Mobile version (current implementation)
  return (
    <div
      ref={cardRef}
      className="relative w-full h-[220px] md:h-[250px]"
      style={{
        perspective: "1000px",
        maxWidth: "100%",
        margin: "0 auto",
        padding: "0.25rem"
      }}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <motion.div
        className="w-full h-full relative"
        style={{ transformStyle: "preserve-3d" }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        initial={{ rotateY: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut", delay: sequence * 0.1 }}
      >
        {/* FRONT SIDE */}
        <div
          className="absolute w-full h-full bg-white rounded-3xl shadow-md p-1"
          style={{ backfaceVisibility: "hidden" }}
        >
          <AnimatePresence>
            {(!isImageLoaded || !isTextLoaded) && (
              <motion.div
                key="skeleton"
                className="absolute inset-0 flex flex-row items-center justify-center bg-white rounded-3xl"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.5 } }}
              >
                {!isImageLoaded && (
                  <SkeletonBlock className="w-1/3 h-full mr-1" />
                )}
                {!isTextLoaded && (
                  <div className="flex flex-col w-2/3">
                    <SkeletonBlock className="w-48 h-6 mb-1" />
                    <SkeletonBlock className="w-56 h-4 mb-1" />
                    <SkeletonBlock className="w-24 h-5 mb-1" />
                    <SkeletonBlock className="w-32 h-6" />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isImageLoaded && isTextLoaded ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-row items-center justify-between h-full w-full"
          >
            {/* Image Container */}
            <div className="w-[45%] h-full flex items-center justify-center">
              {imageUrl && (
                <motion.img
                  custom={0}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  variants={imageVariants}
                  src={imageUrl}
                  alt={productTitle || "Product Image"}
                  className="w-full h-auto max-h-full object-contain"
                  onLoad={() => setIsImageLoaded(true)}
                />
              )}
            </div>

            {/* Content Container */}
            <div className="flex flex-col justify-between h-full w-[55%]">
              {/* Product Title - Mobile */}
              {isTextLoaded && productTitle && (
                <motion.h3
                  custom={0}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  variants={textVariants}
                  className="text-xl md:text-2xl font-bold text-gray-900 tracking-wide text-left w-full"
                  style={{
                    fontFamily: "Geogrotesque, sans-serif",
                    textTransform: "none",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    textAlign: "left",
                    width: "100%",
                    fontSize: (() => {
                      const words = formatProductTitle(productTitle).split(' ');
                      const totalLength = words.join('').length;
                      if (totalLength > 30) {
                        return "clamp(0.75rem, 1.2vw, 1rem)";
                      } else if (totalLength > 20) {
                        return "clamp(0.875rem, 1.4vw, 1.1rem)";
                      } else {
                        return "clamp(1rem, 1.6vw, 1.2rem)";
                      }
                    })(),
                    marginBottom: "0.125rem",
                    lineHeight: "1.1",
                    height: "2em",
                    overflow: "hidden",
                    whiteSpace: "normal"
                  }}
                >
                  {formatProductTitle(productTitle)}
                </motion.h3>
              )}

              {/* Slogan */}
              {isTextLoaded && slogan && (
                <motion.p
                  custom={1}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  variants={textVariants}
                  className="text-sm text-gray-600 text-left w-full"
                  style={{ 
                    fontFamily: "Open Sans, sans-serif",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    lineHeight: "1.1",
                    textAlign: "left",
                    width: "100%",
                    fontSize: "clamp(0.625rem, 1.1vw, 0.75rem)",
                    marginBottom: "0.125rem",
                    height: isProfessional ? "2em" : "1.8em",
                    overflow: "hidden"
                  }}
                >
                  {formatProductTitle(slogan)}
                </motion.p>
              )}

              {/* Price (only for household) */}
              {isTextLoaded && typeof price === "number" && !isProfessional && (
                <motion.p
                  custom={2}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  variants={textVariants}
                  className="text-2xl font-bold text-gray-900 text-left w-full"
                  style={{ 
                    fontFamily: "Geogrotesque, sans-serif",
                    textAlign: "left",
                    width: "100%",
                    fontSize: "clamp(0.875rem, 1.4vw, 1.1rem)",
                    marginBottom: "0.125rem",
                    height: "1.2em"
                  }}
                >
                  ${price.toFixed(2)}
                </motion.p>
              )}

              {/* Button Container */}
              <motion.div
                custom={3}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={textVariants}
                className="w-full mt-auto flex flex-col gap-1"
                style={{ 
                  height: isProfessional ? "2rem" : "4.5rem",
                  justifyContent: "flex-end",
                  paddingBottom: "0.5rem",
                  paddingRight: "0.5rem"
                }}
              >
                {!isProfessional && isTextLoaded && buyNowButtonUrl && (
                  <Button
                    to={buyNowButtonUrl === "Coming Soon!" ? "Coming Soon!" : undefined}
                    href={buyNowButtonUrl !== "Coming Soon!" ? buyNowButtonUrl : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary"
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      if (buyNowButtonUrl !== "Coming Soon!") {
                        window.open(buyNowButtonUrl, '_blank');
                      }
                    }}
                    style={{ 
                      padding: "0.25rem 0.5rem",
                      fontSize: "clamp(0.625rem, 1.1vw, 0.75rem)",
                      height: "1.75rem"
                    }}
                  >
                    {buyNowButtonUrl === "Coming Soon!" ? "Coming Soon!" : "Buy Now"}
                  </Button>
                )}
                {isTextLoaded && sys?.id && (
                  <Link
                    to={`/${isProfessional ? 'industrial' : 'household'}/product/${toUrlFriendly(productTitle)}`}
                    className="w-full"
                  >
                    <Button
                      variant={isProfessional ? "primary" : "secondary"}
                      size="small"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      style={{ 
                        padding: "0.25rem 0.5rem",
                        fontSize: "clamp(0.625rem, 1.1vw, 0.75rem)",
                        height: "1.75rem"
                      }}
                    >
                      More Details
                    </Button>
                  </Link>
                )}
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* BACK SIDE */}
        <div
          className="absolute w-full h-full bg-white rounded-3xl shadow-md p-3 flex flex-col justify-center items-center text-center"
          style={{
            transform: "rotateY(180deg)",
            backfaceVisibility: "hidden",
          }}
        >
          {detailedProductDescription ? (
            <>
              <h3
                className="text-base font-semibold text-gray-900 mb-2"
                style={{ fontFamily: "Geogrotesque, sans-serif" }}
              >
                Product Details
              </h3>
              <p
                className="text-gray-700 leading-relaxed text-xs overflow-auto px-2 line-clamp-6"
                style={{ fontFamily: "Geogrotesque, sans-serif" }}
              >
                {preventOrphan(detailedProductDescription)}
              </p>
            </>
          ) : (
            <div className="flex-grow flex justify-center items-center text-gray-500 text-xs">
              No detailed description available.
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ProductCard;